import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import BarcodeScanner from '../components/BarcodeScanner';
import {
  postProductCompatibility,
  getCompatPrefs,
  upsertCompatPrefs,
  type ProductCompatResponse,
  type CompatPrefsOut,
  COMPAT_ALLERGENS,
} from '../api/endpoints';
import useScanCredits from '../hooks/useScanCredits';
import { useScannerFocusTrap } from '../hooks/useScannerFocusTrap';
import { ProductSlot } from './shared/ProductSlot';
import { FavoriteProductPicker } from './shared/FavoriteProductPicker';
import {
  IconHelp,
  IconSwap,
  IconGear,
  IconLink,
  IconSpark,
} from '../components/icons/AnalyzerIcons';
import WaitingGame from '../components/WaitingGame';
import { type ImageScannerResult } from '../components/ImageScannerButton';
import ImageScannerCard from '../components/ImageScannerCard';
import ProductConfirmationDialog, { type ProductConfirmationData } from '../components/ProductConfirmationDialog';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';
import type { FavoriteProduct } from '../types/api';
import { getErrorMessage } from '../utils/errors';
import {
  areSameProductReferences,
  createProductReference,
  readCompatibilityDraft,
  writeCompatibilityDraft,
  type CompatibilityUsage,
  type ProductPairTransfer,
  type ProductReference,
} from '../utils/productIdentity';

export type { CompatibilityUsage } from '../utils/productIdentity';

export interface ProductCompatInput {
  products?: ProductPairTransfer['products'];
  prefill?: { barcodeA?: string; barcodeB?: string; productIdA?: number; productIdB?: number };
  usage?: { a?: CompatibilityUsage; b?: CompatibilityUsage };
}

export interface ProductCompatResult {
  response: ProductCompatResponse;
  payload: { barcode_a: string; barcode_b: string; context: string };
  productIds: { a?: number; b?: number };
}

const clean = (v: string) => v.replace(/\D/g, '').slice(0, 14);
const labelize = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const spring = { type: 'spring' as const, stiffness: 420, damping: 36, mass: 0.9 };
const pageVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};
const sectionItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: spring },
};

function AnimatedSwap({
  value,
  className,
  as: Tag = 'span',
}: { value: React.ReactNode; className?: string; as?: React.ElementType }) {
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={typeof value === 'string' || typeof value === 'number' ? String(value) : 'swap'}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={{ duration: 0.18 }}
        className={className}
      >
        <Tag>{value}</Tag>
      </motion.span>
    </AnimatePresence>
  );
}

function FlashOnChange({
  children,
  depsKey,
  className,
}: { children: React.ReactNode; depsKey: React.Key; className?: string }) {
  return (
    <motion.div
      key={String(depsKey)}
      initial={{ backgroundColor: 'rgba(42,164,166,0.18)', scale: 0.99 }}
      animate={{ backgroundColor: 'rgba(0,0,0,0)', scale: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const SENS_OPTIONS = [
  { v: 'none', label: 'None' },
  { v: 'possible', label: 'Possible' },
  { v: 'likely', label: 'Likely' },
] as const;

const ETHICAL_OPTIONS = [
  { v: 'off', label: 'Off' },
  { v: 'vegan', label: 'Vegan' },
  { v: 'halal', label: 'Halal' },
  { v: 'cruelty_free', label: 'Cruelty-free' },
] as const;

function Toggle({
  label, hint, checked, onChange,
}: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="card card--compact card--bordered flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>{label}</div>
        {hint && <div className="t-small t-mute">{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => { onChange(!checked); }}
        className="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200"
        style={{ background: checked ? 'var(--c-kivo-teal)' : 'var(--c-charcoal-12)' }}
        aria-pressed={checked}
      >
        <span
          className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200"
          style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  );
}

function CompatPreferencesPage({ onBack }: { onBack: () => void }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [allergies, setAllergies] = useState<string[]>([]);
  const [sensitivity, setSensitivity] = useState<'none' | 'possible' | 'likely'>('likely');
  const [acneProne, setAcneProne] = useState(false);
  const [ethical, setEthical] = useState<'off' | 'vegan' | 'halal' | 'cruelty_free'>('off');
  const [derm, setDerm] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        const p: CompatPrefsOut = await getCompatPrefs();
        const hasTracked = sessionStorage.getItem('telemetry_compat_prefs_viewed');
        if (!hasTracked) {
          const { postCompatPrefsView } = await import('../api/telemetry');
          void postCompatPrefsView().catch(function ignoreError() { /* non-fatal */ });
          sessionStorage.setItem('telemetry_compat_prefs_viewed', '1');
        }
        setAllergies(p.allergies);
        setSensitivity(p.sensitivity);
        setAcneProne(p.acne_prone);
        setEthical(p.ethical_mode);
        setDerm(p.cautious_actives);
      } catch (error: unknown) {
        setErr(getErrorMessage(error, 'Failed to load preferences'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleAllergen = (a: string) =>
    { setAllergies((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a])); };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await upsertCompatPrefs({
        allergies,
        sensitivity,
        acne_prone: acneProne,
        ethical_mode: ethical,
        cautious_actives: derm,
      });
      const { postCompatPrefsUpdate } = await import('../api/telemetry');
      void postCompatPrefsUpdate([]).catch(function ignoreError() { /* non-fatal */ });
      onBack();
    } catch (error: unknown) {
      setErr(getErrorMessage(error, 'Failed to save'));
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl py-4 sm:py-6">
      <div
        className="sticky top-0 z-10 px-4 sm:px-0 py-3"
        style={{ background: 'var(--c-cream-weak)', borderBottom: '1px solid var(--rule)', backdropFilter: 'blur(8px)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <h1 className="t-h3">Compatibility preferences</h1>
          <div className="row-3">
            <button onClick={onBack} className="btn btn--ghost btn--sm">Skip</button>
            <button onClick={save} disabled={saving} className="btn btn--primary btn--sm">
              <AnimatedSwap value={saving ? 'Saving…' : 'Save'} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-0 pt-4">
        <div className="card card--sand card--compact">
          <span className="t-small" style={{ fontWeight: 500 }}>Optional.</span>
          <span className="t-small t-mute"> These tweak how the compatibility engine weighs risk/interactions.</span>
        </div>
      </div>

      <div className="px-4 sm:px-0 py-4 stack-5">
        {loading ? (
          <p className="t-small t-mute">Loading…</p>
        ) : (
          <>
            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Ingredients to avoid</div>
              <p className="t-small t-mute">Select any known allergens to flag risky pairs.</p>
              <div className="flex flex-wrap gap-2">
                {COMPAT_ALLERGENS.map((a) => {
                  const active = allergies.includes(a);
                  return (
                    <button
                      key={a}
                      onClick={() => { toggleAllergen(a); }}
                      className={active ? 'pill pill--teal' : 'pill'}
                      style={{ cursor: 'pointer', transition: 'background var(--t-fast) var(--ease)' }}
                    >
                      {active && <span className="dot" />}
                      <AnimatedSwap value={labelize(a)} />
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Skin sensitivity</div>
              <p className="t-small t-mute">How strict should we be on potential irritants across pairs?</p>
              <div
                className="grid grid-cols-3 w-full overflow-hidden"
                style={{ borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)' }}
              >
                {SENS_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => { setSensitivity(o.v); }}
                    className="px-3 py-2 t-small text-center"
                    style={{
                      background: sensitivity === o.v ? 'var(--c-kivo-teal)' : 'var(--c-cream)',
                      color: sensitivity === o.v ? 'var(--c-cream)' : 'var(--ink)',
                      transition: 'background var(--t-fast) var(--ease)',
                    }}
                  >
                    <AnimatedSwap value={o.label} />
                  </button>
                ))}
              </div>
            </section>

            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Ethical preference</div>
              <p className="t-small t-mute">Pick one.</p>
              <div className="flex flex-wrap gap-2">
                {ETHICAL_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => { setEthical(o.v); }}
                    className={ethical === o.v ? 'pill pill--teal' : 'pill'}
                    style={{ cursor: 'pointer', transition: 'background var(--t-fast) var(--ease)' }}
                  >
                    {ethical === o.v && <span className="dot" />}
                    <AnimatedSwap value={o.label} />
                  </button>
                ))}
              </div>
            </section>

            <section className="grid sm:grid-cols-3 gap-3">
              <Toggle label="Breakout-prone" hint="Prefer non-comedogenic pairs." checked={acneProne} onChange={setAcneProne} />
              <Toggle label="Prefer gentle formulas" hint="Go easy on strong actives." checked={derm} onChange={setDerm} />
            </section>

            <AnimatePresence>
              {err && (
                <motion.div
                  key={err}
                  initial={{ y: -6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -6, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="t-small"
                  style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                >
                  {err}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
}

const ProductCompatScreen: React.FC<ScreenProps<ProductCompatInput, ProductCompatResult>> = ({
  mode,
  input,
  onComplete,
  onCancel,
}) => {
  const nav = useNavigate();
  const prefill = input?.prefill ?? {};
  const draft = mode === 'page' ? readCompatibilityDraft() : null;
  const prefillA = prefill.productIdA || prefill.barcodeA
    ? createProductReference({ productId: prefill.productIdA, barcode: prefill.barcodeA })
    : undefined;
  const prefillB = prefill.productIdB || prefill.barcodeB
    ? createProductReference({ productId: prefill.productIdB, barcode: prefill.barcodeB })
    : undefined;
  const initialProductA = input?.products?.[0] ?? prefillA ?? draft?.products[0] ?? {};
  const initialProductB = input?.products?.[1] ?? prefillB ?? draft?.products[1] ?? {};
  const draftUsageA = areSameProductReferences(initialProductA, draft?.products[0]) ? draft?.usage.a : undefined;
  const draftUsageB = areSameProductReferences(initialProductB, draft?.products[1]) ? draft?.usage.b : undefined;

  const [productA, setProductA] = useState<ProductReference>(initialProductA);
  const [productB, setProductB] = useState<ProductReference>(initialProductB);

  const [scanning, setScanning] = useState<'a' | 'b' | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrefs, setShowPrefs] = useState(false);

  const [gameVisible, setGameVisible] = useState(false);
  const gameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [confirmationProduct, setConfirmationProduct] = useState<ProductConfirmationData | null>(null);
  const [confirmationTarget, setConfirmationTarget] = useState<'a' | 'b' | null>(null);
  const [favoriteTarget, setFavoriteTarget] = useState<'a' | 'b' | null>(null);
  const barcodeInputRefA = useRef<HTMLInputElement>(null);
  const barcodeInputRefB = useRef<HTMLInputElement>(null);

  const [contextA, setContextA] = useState<CompatibilityUsage>(input?.usage?.a ?? draftUsageA ?? 'auto');
  const [contextB, setContextB] = useState<CompatibilityUsage>(input?.usage?.b ?? draftUsageB ?? 'auto');

  const compatContext = useMemo(() => {
    if (contextA === 'auto' || contextB === 'auto') return 'auto';
    const map = { leave_on: 'leaveon', leave_off: 'leaveoff' } as const;
    return `${map[contextA]}_${map[contextB]}`;
  }, [contextA, contextB]);

  // Credit gating (cost comes from the API via the user context — never hardcoded).
  const { total: scansLeft, outOfQuota, isPlusOrPremium } = useScanCredits('product_barcode');

  useEffect(() => () => { if (gameTimerRef.current) clearTimeout(gameTimerRef.current); }, []);

  useEffect(() => {
    if (mode !== 'page') return;
    void (async () => {
      try {
        const p = await getCompatPrefs();
        const looksDefault =
          p.allergies.length === 0 &&
          p.sensitivity === 'likely' &&
          !p.acne_prone &&
          p.ethical_mode === 'off' &&
          !p.cautious_actives;
        const shown = localStorage.getItem('compatPrefsPromptShown');
        if (looksDefault && !shown) {
          localStorage.setItem('compatPrefsPromptShown', '1');
          setShowPrefs(true);
        }
      } catch { /* ignore */ }
    })();
  }, [mode]);

  const barcodeA = productA.barcode ?? '';
  const barcodeB = productB.barcode ?? '';
  const productIdA = productA.productId;
  const productIdB = productB.productId;
  const validA = useMemo(() => clean(barcodeA), [barcodeA]);
  const validB = useMemo(() => clean(barcodeB), [barcodeB]);
  // A side is submittable with a valid barcode OR a known product id
  // (slug-only products from image scan have no barcode).
  const duplicateProducts = areSameProductReferences(productA, productB);
  const canSubmit = (validA.length >= 8 || !!productIdA) &&
    (validB.length >= 8 || !!productIdB) &&
    !duplicateProducts;

  useEffect(() => {
    if (mode !== 'page') return;
    const persistableA = [productA.productId, productA.publicId, productA.sku, productA.barcode, productA.clientId].some(Boolean)
      ? productA
      : undefined;
    const persistableB = [productB.productId, productB.publicId, productB.sku, productB.barcode, productB.clientId].some(Boolean)
      ? productB
      : undefined;
    writeCompatibilityDraft([persistableA, persistableB], { a: contextA, b: contextB });
  }, [mode, productA, productB, contextA, contextB]);

  const pasteInto = async (which: 'a' | 'b') => {
    try {
      const t = await navigator.clipboard.readText();
      const d = clean(t);
      if (!d) return;
      if (which === 'a') setProductA(createProductReference({ barcode: d }));
      else setProductB(createProductReference({ barcode: d }));
    } catch { /* ignore */ }
  };
  const updateBarcode = (which: 'a' | 'b', value: string) => {
    const barcode = clean(value);
    if (which === 'a') setProductA(barcode ? createProductReference({ barcode }) : {});
    else setProductB(barcode ? createProductReference({ barcode }) : {});
  };
  const clearField = (which: 'a' | 'b') => {
    if (which === 'a') setProductA({});
    else setProductB({});
  };

  const swap = () => {
    setProductA(productB);
    setProductB(productA);
    setContextA(contextB);
    setContextB(contextA);
  };

  const handleDetected = useCallback((value: string) => {
    if (outOfQuota) return;
    const digits = clean(value);
    if (!digits) return;
    if (scanning === 'b') {
      const nextProduct = createProductReference({ barcode: digits });
      if (areSameProductReferences(productA, nextProduct)) {
        setError('This product is already selected as Product A.');
        setScanning(null);
        return;
      }
      setProductB(nextProduct);
      setScanning(null);
      setHint(null);
    } else {
      const nextProduct = createProductReference({ barcode: digits });
      if (areSameProductReferences(nextProduct, productB)) {
        setError('This product is already selected as Product B.');
        setScanning(null);
        return;
      }
      setProductA(nextProduct);
      if (!barcodeB) {
        setHint('Now scan the second product');
        setScanning('b');
      } else {
        setScanning(null);
      }
    }
  }, [scanning, barcodeB, outOfQuota, productA, productB]);

  const handleImageScanResult = (result: ImageScannerResult, target: 'a' | 'b') => {
    setConfirmationProduct(result);
    setConfirmationTarget(target);
  };
  const handleImageScanError = (errorMsg: string) => { setError(errorMsg); };

  const handleConfirmProduct = () => {
    if (!confirmationProduct || !confirmationTarget) return;
    const targetRef = confirmationTarget === 'a' ? barcodeInputRefA : barcodeInputRefB;
    const barcode = confirmationProduct.barcode ? clean(confirmationProduct.barcode) : '';
    const nextProduct = createProductReference({
      product_id: confirmationProduct.product_id,
      barcode: barcode.length >= 8 ? barcode : undefined,
      brand: confirmationProduct.brand,
      name: confirmationProduct.name,
      image_url: confirmationProduct.image_url,
    });
    const otherProduct = confirmationTarget === 'a' ? productB : productA;
    if (areSameProductReferences(nextProduct, otherProduct)) {
      setError(`This product is already selected as Product ${confirmationTarget === 'a' ? 'B' : 'A'}.`);
      setConfirmationProduct(null);
      setConfirmationTarget(null);
      return;
    }
    if (confirmationTarget === 'a') {
      setProductA(nextProduct);
    } else {
      setProductB(nextProduct);
    }
    setConfirmationProduct(null);
    setConfirmationTarget(null);
    setError(null);
    if (targetRef.current) {
      targetRef.current.focus();
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const flash = document.createElement('div');
      flash.className = 'absolute inset-0 rounded-xl pointer-events-none';
      flash.style.cssText = 'background:var(--c-teal-weak);animation:fadeOut 0.8s ease-out forwards';
      const inputParent = targetRef.current.parentElement;
      if (inputParent) {
        inputParent.style.position = 'relative';
        inputParent.appendChild(flash);
        setTimeout(() => { flash.remove(); }, 800);
      }
    }
  };
  const handleCancelProduct = () => {
    setConfirmationProduct(null);
    setConfirmationTarget(null);
  };

  const handleFavoriteSelect = (product: FavoriteProduct) => {
    if (!favoriteTarget) return;
    const barcode = product.barcode ? clean(product.barcode) : '';
    const nextProduct = createProductReference({
      product_id: product.product_id,
      barcode: barcode.length >= 8 ? barcode : undefined,
      brand: product.brand,
      name: product.name,
      image_url: product.image_url ?? undefined,
    });
    const otherProduct = favoriteTarget === 'a' ? productB : productA;
    if (areSameProductReferences(nextProduct, otherProduct)) {
      setError(`This product is already selected as Product ${favoriteTarget === 'a' ? 'B' : 'A'}.`);
      return;
    }
    if (favoriteTarget === 'a') setProductA(nextProduct);
    else setProductB(nextProduct);
    setFavoriteTarget(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit || loading || outOfQuota) return;
    setLoading(true);
    setError(null);

    if (gameTimerRef.current) clearTimeout(gameTimerRef.current);
    gameTimerRef.current = setTimeout(() => { setGameVisible(true); }, 600);

    try {
      // Barcode wins when valid; otherwise the side is sent by product id.
      const payload = {
        barcode_a: validA.length >= 8 ? validA : '',
        barcode_b: validB.length >= 8 ? validB : '',
        ...(validA.length < 8 && productIdA ? { product_id_a: productIdA } : {}),
        ...(validB.length < 8 && productIdB ? { product_id_b: productIdB } : {}),
        context: compatContext,
      };
      const res: ProductCompatResponse = await postProductCompatibility(payload);

      const result: ProductCompatResult = { response: res, payload, productIds: { a: productIdA, b: productIdB } };

      if (mode === 'modal') {
        setLoading(false);
        setGameVisible(false);
        onComplete?.(result);
      } else {
        setGameVisible(false);
        nav('/compatibility/result', {
          state: { response: res, payload, productIds: { a: productIdA, b: productIdB } },
        });
      }
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to check compatibility'));
      setGameVisible(false);
      setLoading(false);
    } finally {
      clearTimeout(gameTimerRef.current);
    }
  };

  useEffect(() => { if (outOfQuota) setScanning(null); }, [outOfQuota]);

  const closeScanner = useCallback(() => { setScanning(null); setHint(null); }, []);
  const scanOverlayRef = useScannerFocusTrap<HTMLDivElement>(scanning !== null && !outOfQuota, closeScanner);

  const prefersReduced = useReducedMotion() ?? false;
  const [swapAngle, setSwapAngle] = useState(0);

  const handleSwapAnimated = () => {
    swap();
    setSwapAngle((a) => a + 180);
  };

  useEffect(() => {
    if (scanning) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [scanning]);

  const inner = (
    <AnimatePresence mode="wait" initial={false}>
      {showPrefs ? (
        <motion.div
          key="prefs"
          initial={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          transition={spring}
        >
          <CompatPreferencesPage onBack={() => { setShowPrefs(false); }} />
        </motion.div>
      ) : (
      <motion.div key="main" className="stack-4" variants={pageVariants} initial="hidden" animate="visible">
        {/* Header card */}
        <motion.div variants={sectionItem} className="card card--bordered relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, var(--c-teal-weak) 0%, transparent 50%, var(--c-teal-weak) 100%)' }}
        />
        <div className="relative">
          <div className="row-3 mb-3">
            <div
              className="grid place-items-center w-9 h-9 rounded-full shrink-0"
              style={{ background: 'var(--c-teal-weak)', color: 'var(--c-kivo-teal)' }}
            >
              <IconLink />
            </div>

            {scansLeft !== null && (
              <FlashOnChange depsKey={scansLeft}>
                <span
                  className={outOfQuota ? 'pill' : 'pill pill--teal'}
                  style={outOfQuota ? { color: 'var(--c-error)' } : undefined}
                  title={outOfQuota ? 'Out of credits' : `${scansLeft} credits left`}
                >
                  {outOfQuota
                    ? <><span className="dot dot--coral" /><AnimatedSwap value="0 credits" /></>
                    : <><span className="dot" /><AnimatedSwap value={`${scansLeft} credits`} /></>
                  }
                </span>
              </FlashOnChange>
            )}

            <button
              onClick={() => { setShowPrefs(true); }}
              className="btn btn--ghost btn--sm ml-auto row-3"
            >
              <IconGear /> Preferences
            </button>
          </div>

          <h1 className="t-h3">Product compatibility checker</h1>
          <p className="t-small t-mute mt-1">
            Enter or scan two barcodes. <strong>Product A first</strong>, then B. Use <em>Swap</em> to flip.
            Each card has a <strong>Usage</strong> control — <em>Leave-on</em> (stays on skin, e.g. serum) vs <em>Leave-off</em> (rinses off, e.g. cleanser), or keep <em>Auto</em>.
          </p>
          {mode === 'page' && (
            <button
              type="button"
              onClick={() => { nav('/scanner/how-it-works'); }}
              className="mt-2 inline-flex items-center gap-1 t-small"
              style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline' }}
            >
              <IconHelp /> How this works
            </button>
          )}
        </div>
        </motion.div>

        <motion.div variants={sectionItem}>
          <ImageScannerCard
            onResult={(result, targetField) => {
              handleImageScanResult(result, targetField ?? 'a');
            }}
            onError={handleImageScanError}
            disabled={loading || outOfQuota}
            isPlusOrPremium={isPlusOrPremium}
            showFieldSelector={true}
            fieldALabel="Product A"
            fieldBLabel="Product B"
          />
        </motion.div>

        {/* Input card */}
        <motion.div variants={sectionItem} className="card card--bordered stack-4">
        {outOfQuota ? (
          <div className="text-center stack-3 items-center">
            <div
              className="w-12 h-12 flex items-center justify-center rounded-full mx-auto"
              style={{ background: 'rgba(196,103,58,0.1)', color: 'var(--c-error)' }}
            >✕</div>
            <p className="t-small" style={{ fontWeight: 600, color: 'var(--ink)' }}>You&apos;re all set for today</p>
            <p className="t-small t-mute">You&apos;re out of credits. Top up in your wallet or wait for your monthly reset.</p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
              <button onClick={() => { nav('/plans'); }} className="btn btn--primary btn--sm">Upgrade plan</button>
              <button onClick={() => { if (mode === 'modal') onCancel?.(); else nav('/'); }} className="btn btn--ghost btn--sm">
                {mode === 'modal' ? 'Close' : "I'll try tomorrow"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto_1fr] md:gap-5">
              <ProductSlot
                side="a"
                product={productA}
                inputRef={barcodeInputRefA}
                canScan={!outOfQuota}
                selectedNote="Selected for compatibility"
                onBarcodeChange={(value) => { updateBarcode('a', value); }}
                onPaste={() => pasteInto('a')}
                onScan={() => { setScanning('a'); }}
                onPickFavorite={() => { setFavoriteTarget('a'); }}
                onClear={() => { clearField('a'); }}
                footer={
                  <Segmented
                    side="a"
                    label="Usage"
                    value={contextA}
                    onChange={(v) => { setContextA(v as 'auto' | 'leave_on' | 'leave_off'); }}
                    options={[{ value: 'auto', label: 'Auto' }, { value: 'leave_on', label: 'Leave-on' }, { value: 'leave_off', label: 'Leave-off' }]}
                  />
                }
              />
              <div className="flex items-center justify-center">
                <div className="stack-2 items-center w-full md:w-auto">
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.96 }}
                    onClick={handleSwapAnimated}
                    className="btn btn--ghost btn--sm row-3 w-full md:w-auto justify-center"
                  >
                    <motion.span
                      animate={{ rotate: swapAngle }}
                      transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                      style={{ display: 'inline-flex' }}
                    >
                      <IconSwap />
                    </motion.span>
                    Swap A/B
                  </motion.button>
                  <p className="t-small t-mute text-center">Usage stays with each product</p>
                </div>
              </div>
              <ProductSlot
                side="b"
                product={productB}
                inputRef={barcodeInputRefB}
                canScan={!outOfQuota}
                selectedNote="Selected for compatibility"
                onBarcodeChange={(value) => { updateBarcode('b', value); }}
                onPaste={() => pasteInto('b')}
                onScan={() => { setScanning('b'); }}
                onPickFavorite={() => { setFavoriteTarget('b'); }}
                onClear={() => { clearField('b'); }}
                footer={
                  <Segmented
                    side="b"
                    label="Usage"
                    value={contextB}
                    onChange={(v) => { setContextB(v as 'auto' | 'leave_on' | 'leave_off'); }}
                    options={[{ value: 'auto', label: 'Auto' }, { value: 'leave_on', label: 'Leave-on' }, { value: 'leave_off', label: 'Leave-off' }]}
                  />
                }
              />
            </div>


            <AnimatePresence>
              {(Boolean(error) || duplicateProducts) && (
                <motion.p
                  key={error ?? 'duplicate-products'}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="t-small"
                  style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                >
                  {error ?? 'Choose two different products to check compatibility.'}
                </motion.p>
              )}
            </AnimatePresence>

            {canSubmit && (
              <div className="compare-ready" role="status">
                <span className="dot" /> Ready to check compatibility
              </div>
            )}

            <div className={mode === 'page' ? 'hidden md:flex md:flex-wrap gap-2' : 'flex flex-col sm:flex-row sm:flex-wrap gap-2'}>
              <motion.button
                type="button"
                whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
                onClick={handleSubmit}
                disabled={!canSubmit || loading || outOfQuota}
                className="btn btn--primary row-3 w-full sm:w-auto justify-center"
              >
                <IconSpark /> <AnimatedSwap value={loading ? 'Checking…' : 'Run compatibility'} />
              </motion.button>
              <span className="t-small t-mute self-center">EAN-8 / UPC-A / EAN-13 / GTIN-14 supported.</span>
            </div>
          </>
        )}
        </motion.div>

        {mode === 'page' && !outOfQuota && (
          <div className="compare-mobile-cta md:hidden" data-testid="compat-mobile-cta">
            <div className="t-small" style={{ fontWeight: 600, color: canSubmit ? 'var(--c-deep-teal)' : 'var(--ink-muted)' }}>
              {canSubmit ? 'Ready to check compatibility' : 'Select two different products'}
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              className="btn btn--primary row-3 w-full justify-center"
            >
              <IconSpark /> {loading ? 'Checking…' : 'Run compatibility'}
            </button>
          </div>
        )}

        {/* Barcode scanner overlay */}
        <AnimatePresence>
        {scanning && !outOfQuota && (
          <motion.div
            key="overlay"
            ref={scanOverlayRef}
            tabIndex={-1}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-50 bg-black/80 overscroll-none outline-none"
            aria-modal="true"
            role="dialog"
            aria-label={scanning === 'a' ? 'Scan first product barcode' : 'Scan second product barcode'}
          >
            <div className="absolute inset-0 flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 text-white bg-black/40 backdrop-blur-sm">
                <FlashOnChange depsKey={scanning}>
                  <div className="t-small" style={{ color: 'rgba(248,244,238,0.9)' }}>
                    <AnimatedSwap value={scanning === 'a' ? 'Scan first product' : 'Scan second product'} />
                  </div>
                </FlashOnChange>
                <div className="row-3">
                  <AnimatePresence>
                    {hint && (
                      <motion.span
                        key={hint}
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.18 }}
                        className="t-small"
                        style={{ color: 'rgba(248,244,238,0.7)' }}
                      >
                        {hint}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  <button
                    type="button"
                    onClick={closeScanner}
                    className="btn btn--hud btn--sm"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="flex-1 min-h-0">
                <div className="h-full w-full relative">
                  <div className="absolute inset-0">
                    <BarcodeScanner onDetected={handleDetected} onFallback={() => { setScanning(null); }} />
                  </div>
                  <div className="absolute inset-0 pointer-events-none grid place-items-center">
                    <div className="w-[min(92vw,520px)] aspect-[1.6/1] max-h-[70vh] relative">
                      <div className="absolute inset-0 rounded-2xl ring-2 ring-white/70" />
                      <div className="absolute -top-1 -left-1 h-6 w-6 border-t-4 border-l-4 border-white/80 rounded-tl-lg" />
                      <div className="absolute -top-1 -right-1 h-6 w-6 border-t-4 border-r-4 border-white/80 rounded-tr-lg" />
                      <div className="absolute -bottom-1 -left-1 h-6 w-6 border-b-4 border-l-4 border-white/80 rounded-bl-lg" />
                      <div className="absolute -bottom-1 -right-1 h-6 w-6 border-b-4 border-r-4 border-white/80 rounded-br-lg" />
                      <motion.div
                        className="absolute left-0 right-0 h-px pointer-events-none"
                        style={{
                          background: 'linear-gradient(to right, transparent, var(--c-kivo-teal), transparent)',
                          boxShadow: '0 0 16px rgba(42,164,166,0.85)',
                        }}
                        animate={{ top: ['6%', '94%', '6%'] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <FlashOnChange depsKey={scanning}>
                <div className="px-4 py-3 text-center bg-black/40 backdrop-blur-sm">
                  <span className="t-small" style={{ color: 'rgba(248,244,238,0.85)' }}>
                    <AnimatedSwap value="Align the barcode within the frame" />
                  </span>
                </div>
              </FlashOnChange>
            </div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* Waiting game */}
        {gameVisible && loading && (
          <WaitingGame onExit={() => { setGameVisible(false); }} />
        )}

        {confirmationProduct && confirmationTarget && (
          <ProductConfirmationDialog
            product={confirmationProduct}
            onConfirm={handleConfirmProduct}
            onCancel={handleCancelProduct}
          />
        )}
        {favoriteTarget && (
          <FavoriteProductPicker
            targetLabel={favoriteTarget === 'a' ? 'Product A' : 'Product B'}
            excludedProduct={favoriteTarget === 'a' ? productB : productA}
            onSelect={handleFavoriteSelect}
            onClose={() => { setFavoriteTarget(null); }}
          />
        )}
      </motion.div>
      )}
    </AnimatePresence>
  );

  if (mode === 'modal') {
    return (
      <ModalShell title="Product compatibility" onClose={() => onCancel?.()} widthClass="max-w-4xl">
        <div className="p-4 sm:p-5">{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

function Segmented({
  side, label, value, onChange, options,
}: {
  side: 'a' | 'b';
  label: string; value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const cols = options.length || 1;
  const labelId = `usage-${side}-label`;
  return (
    <div className={`stack-2 compare-usage compare-usage--${side}`}>
      <div id={labelId} className="t-small compare-result-side__label" style={{ fontWeight: 600 }}>
        <span className="compare-result-side__marker" aria-hidden />{label}
      </div>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="grid w-full overflow-hidden compare-usage__options"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)' }}
      >
        {options.map((o, i) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              onClick={() => { onChange(o.value); }}
              onKeyDown={(e) => {
                const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
                  : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
                if (!dir) return;
                e.preventDefault();
                const next = (i + dir + options.length) % options.length;
                onChange(options[next].value);
                (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
              }}
              className={`w-full px-3 sm:px-4 py-2.5 t-small text-center whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--c-kivo-teal)] compare-usage__option ${active ? 'compare-usage__option--active' : ''}`}
              style={{ borderLeft: i > 0 ? '1px solid var(--rule)' : undefined }}
            >
              <AnimatedSwap value={o.label} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ProductCompatScreen;
